// sketchScene.js
// Firestore-safe Excalidraw scene helpers.

export const SKETCH_SCENE_VERSION = 1;

/** Marks an encoded inner array so Firestore never sees array-in-array. */
export const SKETCH_NESTED_ARRAY_KEY = '__a';

const EMPTY_CAMERA = Object.freeze({
  scrollX: 0,
  scrollY: 0,
  zoom: Object.freeze({ value: 1 }),
});

/**
 * @param {unknown} value
 * @returns {string}
 */
function cleanString(value) {
  return String(value || '').trim();
}

/**
 * Drops undefined / non-finite values so the scene can go through updateDoc.
 *
 * @param {unknown} value
 * @returns {unknown}
 */
export function toFirestoreValue(value) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === 'function' || typeof value === 'symbol') return undefined;
  if (typeof value === 'number' && !Number.isFinite(value)) return null;
  if (Array.isArray(value)) {
    return value
      .map((entry) => toFirestoreValue(entry))
      .filter((entry) => entry !== undefined);
  }
  if (value && typeof value === 'object') {
    const next = {};
    Object.entries(value).forEach(([key, entry]) => {
      const cleaned = toFirestoreValue(entry);
      if (cleaned !== undefined) next[key] = cleaned;
    });
    return next;
  }
  return value;
}

/**
 * Firestore cannot store nested arrays (Excalidraw `points: [[x, y], …]`).
 *
 * @param {unknown} value
 * @returns {unknown}
 */
export function encodeSketchNestedArrays(value) {
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (Array.isArray(item)) {
        return {
          [SKETCH_NESTED_ARRAY_KEY]: item.map((entry) => encodeSketchNestedArrays(entry)),
        };
      }
      return encodeSketchNestedArrays(item);
    });
  }
  if (value && typeof value === 'object') {
    const next = {};
    Object.entries(value).forEach(([key, entry]) => {
      next[key] = encodeSketchNestedArrays(entry);
    });
    return next;
  }
  return value;
}

/**
 * Restores Excalidraw nested arrays from a Firestore scene.
 *
 * @param {unknown} value
 * @returns {unknown}
 */
export function decodeSketchNestedArrays(value) {
  if (Array.isArray(value)) {
    return value.map((item) => decodeSketchNestedArrays(item));
  }
  if (value && typeof value === 'object') {
    if (Array.isArray(value[SKETCH_NESTED_ARRAY_KEY])) {
      return value[SKETCH_NESTED_ARRAY_KEY].map((entry) => decodeSketchNestedArrays(entry));
    }
    const next = {};
    Object.entries(value).forEach(([key, entry]) => {
      next[key] = decodeSketchNestedArrays(entry);
    });
    return next;
  }
  return value;
}

/**
 * @param {unknown} elements
 * @returns {object[]}
 */
export function normalizeSketchElements(elements) {
  if (!Array.isArray(elements)) return [];
  return elements
    .filter((element) => element && typeof element === 'object')
    .map((element) => toFirestoreValue(element))
    .filter((element) => element && typeof element === 'object');
}

/**
 * @param {unknown} elements
 * @returns {boolean}
 */
export function isSketchSceneEmpty(elements) {
  return !normalizeSketchElements(elements).some((element) => element.isDeleted !== true);
}

/**
 * @param {unknown} appState
 * @returns {{ scrollX: number, scrollY: number, zoom: { value: number } }}
 */
export function pickSketchCamera(appState) {
  const source = appState && typeof appState === 'object' ? appState : {};
  const zoomValue = Number(source?.zoom?.value);
  return {
    scrollX: Number.isFinite(Number(source.scrollX)) ? Number(source.scrollX) : 0,
    scrollY: Number.isFinite(Number(source.scrollY)) ? Number(source.scrollY) : 0,
    zoom: {
      value: Number.isFinite(zoomValue) && zoomValue > 0 ? zoomValue : 1,
    },
  };
}

/**
 * @param {unknown} files
 * @returns {object}
 */
export function normalizeSketchFileMeta(files) {
  if (!files || typeof files !== 'object' || Array.isArray(files)) return {};

  const next = {};
  Object.entries(files).forEach(([fileId, entry]) => {
    const id = cleanString(fileId);
    if (!id || !entry || typeof entry !== 'object') return;
    const storagePath = cleanString(entry.storagePath);
    if (!storagePath) return;
    next[id] = {
      mimeType: cleanString(entry.mimeType) || 'image/png',
      storagePath,
    };
  });
  return next;
}

/**
 * @param {unknown} scene
 * @returns {{
 *   version: number,
 *   elements: object[],
 *   appState: { scrollX: number, scrollY: number, zoom: { value: number } },
 *   files: object,
 * }}
 */
export function normalizeSketchScene(scene) {
  const decoded = decodeSketchNestedArrays(
    scene && typeof scene === 'object' ? scene : {},
  );
  const source = decoded && typeof decoded === 'object' && !Array.isArray(decoded)
    ? decoded
    : {};
  return {
    version: SKETCH_SCENE_VERSION,
    elements: normalizeSketchElements(source.elements),
    appState: pickSketchCamera(source.appState),
    files: normalizeSketchFileMeta(source.files),
  };
}

/**
 * Scene payload safe for Firestore updateDoc.
 *
 * @param {unknown} scene
 * @returns {object}
 */
export function serializeSketchScene(scene) {
  return encodeSketchNestedArrays(toFirestoreValue(normalizeSketchScene(scene)));
}

/**
 * @returns {{
 *   version: number,
 *   elements: object[],
 *   appState: { scrollX: number, scrollY: number, zoom: { value: number } },
 *   files: object,
 * }}
 */
export function createEmptySketchScene() {
  return {
    version: SKETCH_SCENE_VERSION,
    elements: [],
    appState: { ...EMPTY_CAMERA, zoom: { ...EMPTY_CAMERA.zoom } },
    files: {},
  };
}
